import { useTranslation } from 'next-i18next'
import Head from 'next/head'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React from 'react'

import { SITE_URL } from '@goodfellas/environment'

import {
  IOS_DOWNLOAD_URL,
  PLAY_STORE_URL,
  androidIntentUrl,
} from '../../constants/appLinks'
import { LandingFooter } from '../landing/Closing'
import s from '../landing/landing.module.css'
import { HrefLangs, localePath } from '../seo/HrefLangs'
import { AppleSmartBanner } from './AppleSmartBanner'
import { usePlatform } from './platform'

export interface AppGatePageProps {
  /** Chave do namespace `gates` com kicker/título/lead/ticks/SEO da rota. */
  gateKey: string
  /** Quantidade de ticks (`tick1..N`) definidos no namespace. */
  ticks?: number
  /**
   * Conteúdo efêmero/pessoal (convite de treino, desafio, chat) não deve ser
   * indexado; páginas de feature (ranking, treinos) sim.
   */
  noindex?: boolean
}

/**
 * Página web para rotas que existem no app (AASA/assetlinks): em vez de 404,
 * o deep link vira uma mini-landing da feature convidando a continuar no app
 * — Android abre o app (ou Play Store) via intent://, iOS vai para o
 * TestFlight (destino oficial enquanto não há listagem na App Store; quando
 * houver, o Smart App Banner cuida do open-in-app).
 */
export function AppGatePage({
  gateKey,
  ticks = 0,
  noindex = false,
}: AppGatePageProps) {
  const { t } = useTranslation('gates')
  const router = useRouter()
  const platform = usePlatform()

  const base = SITE_URL.replace(/\/+$/, '')
  const path = router.asPath.split('?')[0]
  const canonical = base + localePath(path, router.locale, router.defaultLocale)

  const openHref =
    platform === 'android' ? androidIntentUrl(canonical) : IOS_DOWNLOAD_URL

  const seoTitle = t(`${gateKey}.seoTitle`)
  const seoDescription = t(`${gateKey}.seoDescription`)

  return (
    <div className={s.page}>
      <Head>
        <title>{`${seoTitle} | Goodfellas`}</title>
        <meta name="description" content={seoDescription} />
        {noindex ? (
          <meta name="robots" content="noindex, follow" />
        ) : (
          <link rel="canonical" href={canonical} />
        )}
        <meta property="og:title" content={`${seoTitle} | Goodfellas`} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={`${base}/images/og-image.jpg`} />
        <meta name="twitter:card" content="summary_large_image" />
      </Head>
      {!noindex && <HrefLangs path={path} />}
      <AppleSmartBanner url={canonical} />

      <div
        className="bg-brand"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <nav className={s.nav} aria-label={t('common.navAria')}>
          <Link className={s.wordmark} href="/">
            Goodfellas
          </Link>
          <Link
            className={`${s.btn} ${s.btnW}`}
            href="/#baixar"
            style={{ padding: '12px 22px' }}
          >
            {t('common.downloadApp')}
          </Link>
        </nav>

        <div
          className={s.ghost}
          style={{
            fontSize: 'clamp(200px,30vw,520px)',
            top: '30%',
            left: '-4%',
          }}
          aria-hidden="true"
        >
          OSS
        </div>

        <main
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: '60px 7vw 90px',
            position: 'relative',
            maxWidth: 900,
          }}
        >
          <p className={`${s.kick} text-white`}>
            <span className={s.kbar} />
            {t(`${gateKey}.kicker`)}
          </p>
          <h1
            className={`${s.d} text-white`}
            style={{ fontSize: 'clamp(52px,8vw,120px)', margin: '24px 0 0' }}
          >
            {t(`${gateKey}.titleSolid`)}
            <br />
            <span className={s.outW}>{t(`${gateKey}.titleOutline`)}</span>
          </h1>
          <p
            className={s.lead}
            style={{
              fontSize: 19,
              marginTop: 20,
              color: 'rgba(255,255,255,.85)',
            }}
          >
            {t(`${gateKey}.lead`)}
          </p>

          {ticks > 0 && (
            <div
              className="text-white"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                marginTop: 22,
              }}
            >
              {Array.from({ length: ticks }, (_, i) => (
                <div className={s.tickrow} key={i}>
                  <span className={s.tick} style={{ background: '#141414' }}>
                    ✓
                  </span>
                  {t(`${gateKey}.tick${i + 1}`)}
                </div>
              ))}
            </div>
          )}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
              marginTop: 36,
            }}
          >
            <a
              className={`${s.btn} ${s.btnW}`}
              href={openHref}
              {...(openHref.startsWith('https://')
                ? { target: '_blank', rel: 'noopener noreferrer' }
                : {})}
            >
              {platform === 'android'
                ? t('common.openInApp')
                : t('common.downloadApp')}
            </a>
            <a
              className={`${s.btn} ${s.btnO}`}
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('common.googlePlay')}
            </a>
            <a
              className={`${s.btn} ${s.btnO}`}
              href={IOS_DOWNLOAD_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('common.appStore')}
            </a>
            <span
              className={`${s.cond} text-white`}
              style={{ fontSize: 14, letterSpacing: '.14em' }}
            >
              {t('common.free')}
            </span>
          </div>
        </main>
      </div>

      <LandingFooter />
    </div>
  )
}
